'use client';

import { useEffect, useState } from 'react';
import { Box, Text, Button, Flex, Theme } from '@radix-ui/themes';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@trashlab/sitesapp/src/components/ui/dialog';
import { TLSiteCartData } from '@trashlab/api/src/domains/consumer/types';
import { signatureStyleImportExpresion } from '@trashlab/api/src/domains/contracts/utils/signature';
import { useEditor, EditorContent } from '@tiptap/react';
import { useVariantValue } from '@trashlab/sitesapp/src/lib/utils/get-variant-value';
import StarterKit from '@tiptap/starter-kit';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import validations from '@trashlab/api/src/domains/consumer/validations/index';
import {
  trpcConsumerSubmitServiceAgreement,
  trpcContractsGetContractTemplateIdForOnlineOrder,
  trpcOrdersGetPhotoSignedUrlByS3Path,
  trpcContractsGetTemplateById,
} from '@trashlab/api/src/generated/hooks';
import { z } from 'zod';

interface ServiceAgreementModalProps {
  cartData: TLSiteCartData;
  onSign?: (data: {
    firstName: string;
    lastName: string;
    ip?: string | null;
  }) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: typeof trpcContractsGetTemplateById.output;
  pdfUrl?: string;
  isLoading?: boolean;
  onSubmit?: (
    formData: z.infer<typeof validations.submitServiceAgreement>,
  ) => Promise<void>;
  primaryColor?: string;
  /** The contract template ID fetched from shop configuration - must be passed to save to cart */
  templateId?: string | null;
}

export function ServiceAgreementModal({
  cartData,
  onSign,
  open,
  onOpenChange,
  template,
  pdfUrl,
  isLoading = false,
  onSubmit,
  primaryColor,
  templateId,
}: ServiceAgreementModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const content = template?.template?.content || undefined;

  const tiptapEditor = useEditor(
    {
      extensions: [
        StarterKit.configure({ underline: false, trailingNode: false }),
      ],
      content: content || undefined,
      editable: false,
    },
    [template?.template?.type, content],
  );

  const form = useForm({
    resolver: zodResolver(validations.submitServiceAgreement),
    defaultValues: {
      cartId: cartData.cart.id,
      tenantId: cartData.cart.tenantId,
      // Use templateId from props (fetched from shop config) if available, otherwise fall back to cart
      contractTemplateId:
        templateId || cartData.cart.contractTemplateId || null,
      signedFirstName: cartData.cart.signedFirstName || '',
      signedLastName: cartData.cart.signedLastName || '',
      signedIp: cartData.cart.signedIp || null,
      signedAtTs: null as string | null,
      signedStyle: cartData.cart.signedStyle || 'signature-style-4',
    },
  });

  // Update form when templateId is fetched from shop configuration
  // This ensures the contractTemplateId is saved to the cart even if it wasn't there initially
  useEffect(() => {
    if (templateId) {
      form.setValue('contractTemplateId', templateId);
    }
  }, [templateId, form]);

  const getIp = async () => {
    try {
      const res = await fetch('/api/get-ip');
      const data = await res.json();
      form.setValue('signedIp', data.ip);
    } catch (err) {
      console.error('Failed to get IP address:', err);
    }
  };

  useEffect(() => {
    if (open) {
      getIp();
    }
  }, [open]);

  const handleSignAgreement = async () => {
    const valid = await form.trigger();

    if (
      !form.getValues('signedFirstName') ||
      !form.getValues('signedLastName')
    ) {
      return;
    }

    if (valid) {
      setIsSubmitting(true);
      try {
        const formValues = form.getValues();
        formValues.signedAtTs = new Date().toISOString();

        if (onSubmit) {
          await onSubmit(validations.submitServiceAgreement.parse(formValues));
        }

        if (onSign) {
          onSign({
            firstName: formValues.signedFirstName,
            lastName: formValues.signedLastName,
            ip: formValues.signedIp,
          });
        }

        onOpenChange(false);
      } catch (error) {
        console.error('Error submitting service agreement:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Theme appearance="light">
        <DialogContent className="max-w-5xl max-h-[90vh] bg-white text-gray-900">
          <style>{signatureStyleImportExpresion}</style>

          <DialogHeader className="flex-none">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Sign the service agreement
            </DialogTitle>
          </DialogHeader>

          <div className="service-agreement-modal grid grid-rows-[1fr_auto] gap-4 h-[calc(90vh-140px)] overflow-hidden">
            <div className="overflow-auto px-6 border border-gray-200 rounded-md bg-white">
              {template ? (
                <>
                  {template?.template?.type === 'pdf' && pdfUrl ? (
                    <Flex direction="column" gap="2">
                      <Text className="text-gray-900">
                        Please review the{' '}
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Service agreement
                        </a>
                      </Text>
                    </Flex>
                  ) : null}

                  {!!content &&
                  template?.template?.type === 'doc' &&
                  tiptapEditor ? (
                    <div className="tiptap-content prose max-w-none text-gray-900">
                      <EditorContent editor={tiptapEditor} />
                    </div>
                  ) : null}
                </>
              ) : isLoading ? (
                <Flex justify="center" align="center" py="4">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </Flex>
              ) : (
                <Text className="text-gray-900">
                  No terms of service required for this order. Please click
                  continue.
                </Text>
              )}
            </div>

            <div className="px-6 pt-4 border-t border-gray-200 bg-white">
              <Box className="grid grid-cols-2 gap-6 mb-6">
                <Box>
                  <Text
                    as="label"
                    className="block text-sm font-medium text-gray-900 mb-1"
                  >
                    First Name <span className="text-red-500">*</span>
                  </Text>
                  <input
                    id="signedFirstName"
                    className="w-full p-2 mt-1 border border-gray-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white text-gray-900 placeholder-gray-500"
                    placeholder="Enter first name"
                    {...form.register('signedFirstName', { required: true })}
                  />
                  {form.formState.errors.signedFirstName && (
                    <Text className="mt-1 text-sm text-red-500">
                      First name is required
                    </Text>
                  )}
                </Box>

                <Box>
                  <Text
                    as="label"
                    className="block text-sm font-medium text-gray-900 mb-1"
                  >
                    Last Name <span className="text-red-500">*</span>
                  </Text>
                  <input
                    id="signedLastName"
                    className="w-full p-2 mt-1 border border-gray-300 rounded-md outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white text-gray-900 placeholder-gray-500"
                    placeholder="Enter last name"
                    {...form.register('signedLastName', { required: true })}
                  />
                  {form.formState.errors.signedLastName && (
                    <Text className="mt-1 text-sm text-red-500">
                      Last name is required
                    </Text>
                  )}
                </Box>
              </Box>

              <Box className="border border-gray-200 rounded-lg p-6 mb-6 bg-white">
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  className="h-20"
                >
                  <Text
                    className={`text-4xl ${form.watch(
                      'signedStyle',
                    )} text-gray-900`}
                  >
                    {form.watch('signedFirstName')}{' '}
                    {form.watch('signedLastName')}
                  </Text>
                </Flex>
              </Box>

              <DialogFooter className="gap-3">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    isLoading ||
                    isSubmitting ||
                    !form.formState.isValid ||
                    !form.watch('signedFirstName') ||
                    !form.watch('signedLastName')
                  }
                  onClick={handleSignAgreement}
                  style={{
                    backgroundColor: primaryColor,
                  }}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Signing...' : 'Sign Agreement'}
                </button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Theme>
    </Dialog>
  );
}

export function ServiceAgreementModalProvider({
  cartData,
  open,
  onOpenChange,
  onSign,
}: {
  cartData: TLSiteCartData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSign?: () => void;
}) {
  const primaryColor = useVariantValue('THEME_PRIMARY_COLOR', '#3463F8');

  const submitMutation = trpcConsumerSubmitServiceAgreement.useMutation();

  const { data: templateId, isLoading: isTemplateIdLoading } =
    trpcContractsGetContractTemplateIdForOnlineOrder.useQuery({
      tenantId: cartData.cart.organization.id,
    });

  const { data: template, isLoading: isTemplateLoading } =
    trpcContractsGetTemplateById.useQuery({
      id: templateId,
    });

  const pdfUrlQuery = trpcOrdersGetPhotoSignedUrlByS3Path.useQuery({
    s3path: template?.template?.s3path,
  });

  const isLoading =
    isTemplateIdLoading || isTemplateLoading || pdfUrlQuery.isLoading;

  const handleSubmit = async (
    formData: z.infer<typeof validations.submitServiceAgreement>,
  ) => {
    if (formData.signedAtTs) {
      await submitMutation.mutateAsync(formData);
    } else {
      console.error(
        'Cannot submit service agreement: Missing signature timestamp',
      );
    }
  };

  return (
    <ServiceAgreementModal
      cartData={cartData}
      onSign={(data) => {
        if (onSign) onSign();
      }}
      open={open}
      onOpenChange={onOpenChange}
      template={template}
      pdfUrl={pdfUrlQuery.data?.url}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      primaryColor={primaryColor}
      templateId={templateId}
    />
  );
}

export function useServiceAgreementModal(cartData: TLSiteCartData) {
  const [isOpen, setIsOpen] = useState(false);
  const [onSignCallback, setOnSignCallback] = useState<
    (() => void) | undefined
  >(undefined);

  const openModal = (onSign?: () => void) => {
    setOnSignCallback(() => onSign);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return {
    ServiceAgreementModal: (
      <ServiceAgreementModalProvider
        cartData={cartData}
        open={isOpen}
        onOpenChange={setIsOpen}
        onSign={onSignCallback}
      />
    ),
    openModal,
    closeModal,
    isOpen,
  };
}
